import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLoaderData, useSubmit } from "react-router-dom";
import { Settings } from "./types";

// Long-press threshold in ms (mobile)
const LONG_PRESS_MS = 500;

export default function WorkOrderSettingForm() {
    const [curSettings, setCurSettings] = useState<Settings>(useLoaderData());
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const submit = useSubmit();

    // Auto-focus whenever a field opens
    useEffect(() => {
        if (editingKey) inputRef.current?.focus();
    }, [editingKey]);

    const handleSubmit = () => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(curSettings));
        submit(formData, { method: "post" });
    };

    // ── Shared inline-editable field ─────────────────────────────────────────
    const EditableField = ({
        id,
        value,
        type = "text",
        className = "form-control inline",
        viewClassName = "form-control-plaintext inline",
        onChange,
    }: {
        id: string;
        value: string;
        type?: string;
        className?: string;
        viewClassName?: string;
        onChange: (val: string) => void;
    }) => {
        const isEditing = editingKey === id;
        const isMobile = navigator.maxTouchPoints > 0;

        const activate = (e: React.SyntheticEvent) => {
            e.stopPropagation();
            setEditingKey(id);
        };

        const handleTouchStart = (e: React.TouchEvent) => {
            e.stopPropagation();
            longPressTimer.current = setTimeout(() => {
                setEditingKey(id);
            }, LONG_PRESS_MS);
        };

        const cancelLongPress = () => {
            if (longPressTimer.current) {
                clearTimeout(longPressTimer.current);
                longPressTimer.current = null;
            }
        };

        const handleBlur = () => {
            setEditingKey(null);
            handleSubmit();
        };

        if (isEditing) {
            return (
                <input
                    ref={inputRef}
                    type={type}
                    className={className}
                    value={value}
                    onChange={e => onChange(e.currentTarget.value)}
                    onBlur={handleBlur}
                    onKeyDown={e => {
                        if (e.key === "Enter") { setEditingKey(null); handleSubmit(); }
                        if (e.key === "Escape") setEditingKey(null);
                    }}
                    onClick={e => e.stopPropagation()}
                />
            );
        }

        return (
            <span
                className={viewClassName}
                style={{ cursor: isMobile ? "default" : "text", borderBottom: "1px dashed #adb5bd" }}
                title={isMobile ? "Hold to edit" : "Double-click to edit"}
                // Desktop: double-click
                onDoubleClick={!isMobile ? activate : undefined}
                // Mobile: long-press
                onTouchStart={isMobile ? handleTouchStart : undefined}
                onTouchEnd={isMobile ? cancelLongPress : undefined}
                onTouchMove={isMobile ? cancelLongPress : undefined}
            >
                {value || <em className="text-muted">{isMobile ? "hold to edit" : "double-click to edit"}</em>}
            </span>
        );
    };

    return (
        <div>
            <input className="nodisplay" name="data" readOnly value={JSON.stringify(curSettings)} />
            <details className="m-3">
                <summary className="mt-3 h1">Process <span className="h5 text-secondary fst-italic">(double-click to edit)</span></summary>
                <ul>
                    {curSettings?.proc.map((p, k) =>
                        <li key={k} className="my-5 h2">
                            <details>
                                <summary>
                                    <EditableField
                                        id={`cat-${k}`}
                                        value={p.category}
                                        viewClassName="form-control-plaintext inline fw-bold"
                                        onChange={val => setCurSettings({ ...curSettings, proc:
                                            curSettings.proc.map((p, pk) => pk === k ? { ...p, category: val } : p)
                                        })}
                                    />
                                    <button
                                        children="Delete"
                                        type="button"
                                        className="btn btn-danger ms-3"
                                        onClick={() => {
                                            if ((p.category || p.data.length) && !window.confirm("Are you sure to delete category?")) return;
                                            setCurSettings(curSettings => ({ ...curSettings, proc: curSettings.proc.filter((_, pk) => pk !== k) }));
                                            handleSubmit();
                                        }}
                                    />
                                </summary>
                                <ul>
                                    {p.data.map((pp, kk) =>
                                        <li className="my-3" key={kk}>
                                            <details>
                                                <summary>
                                                    <EditableField
                                                        id={`proc-${k}-${kk}`}
                                                        value={pp.proc}
                                                        onChange={val => setCurSettings({ ...curSettings, proc:
                                                            curSettings.proc.map((p, pk) => pk === k ? { ...p, data:
                                                                p.data.map((pp, ppk) => ppk === kk ? { ...pp, proc: val } : pp)
                                                            } : p)
                                                        })}
                                                    />
                                                    <span className="ms-2">&mdash;</span>
                                                    <EditableField
                                                        id={`date-${k}-${kk}`}
                                                        value={pp.date || ""}
                                                        type="date"
                                                        className="form-control inline ms-2"
                                                        viewClassName="form-control-plaintext inline ms-2"
                                                        onChange={val => setCurSettings({ ...curSettings, proc:
                                                            curSettings.proc.map((p, pk) => pk === k ? { ...p, data:
                                                                p.data.map((pp, ppk) => ppk === kk ? { ...pp, date: val } : pp)
                                                            } : p)
                                                        })}
                                                    />
                                                    <button
                                                        children="Delete"
                                                        type="button"
                                                        className="btn btn-danger ms-3"
                                                        onClick={() => {
                                                            if ((pp.proc || pp.spec.length) && !window.confirm("Are you sure to delete this process?")) return;
                                                            setCurSettings(curSettings => ({ ...curSettings, proc:
                                                                curSettings.proc.map((p, pk) => pk === k ? { ...p, data:
                                                                    p.data.filter((_, ppk) => ppk !== kk)
                                                                } : p)
                                                            }));
                                                            handleSubmit();
                                                        }}
                                                    />
                                                </summary>
                                                <ul>
                                                    {pp.spec.map((s, kkk) =>
                                                        <li className="my-3" key={kkk}>
                                                            <EditableField
                                                                id={`spec-${k}-${kk}-${kkk}`}
                                                                value={s}
                                                                className="form-control"
                                                                viewClassName="form-control-plaintext"
                                                                onChange={val => setCurSettings({ ...curSettings, proc:
                                                                    curSettings.proc.map((p, pk) => pk === k ? { ...p, data:
                                                                        p.data.map((pp, ppk) => ppk === kk ? { ...pp, spec:
                                                                            pp.spec.map((ppp, pppk) => pppk === kkk ? val : ppp)
                                                                        } : pp)
                                                                    } : p)
                                                                })}
                                                            />
                                                            <button
                                                                children="Delete"
                                                                type="button"
                                                                className="btn btn-danger ms-3"
                                                                onClick={() => {
                                                                    if (s && !window.confirm("Are you sure to delete this spec?")) return;
                                                                    setCurSettings(curSettings => ({ ...curSettings, proc:
                                                                        curSettings.proc.map((p, pk) => pk === k ? { ...p, data:
                                                                            p.data.map((pp, ppk) => ppk === kk ? { ...pp, spec:
                                                                                pp.spec.filter((_, pppk) => pppk !== kkk)
                                                                            } : pp)
                                                                        } : p)
                                                                    }));
                                                                    handleSubmit();
                                                                }}
                                                            />
                                                        </li>
                                                    )}
                                                    <li className="my-3">
                                                        <button
                                                            children="Add Spec"
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={() => {
                                                                const newIdx = pp.spec.length;
                                                                setCurSettings({ ...curSettings, proc:
                                                                    curSettings.proc.map((p, pk) => pk === k ? { ...p, data:
                                                                        p.data.map((pp, ppk) => ppk === kk ? { ...pp, spec: [...pp.spec, ""] } : pp)
                                                                    } : p)
                                                                });
                                                                setEditingKey(`spec-${k}-${kk}-${newIdx}`);
                                                            }}
                                                        />
                                                    </li>
                                                </ul>
                                            </details>
                                        </li>
                                    )}
                                    <li className="my-3">
                                        <button
                                            children="Add Process Number"
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => {
                                                const newIdx = p.data.length;
                                                setCurSettings({ ...curSettings, proc:
                                                    curSettings.proc.map((p, pk) => pk === k ? { ...p, data:
                                                        [...p.data, { proc: "", date: "", spec: [] }]
                                                    } : p)
                                                });
                                                setEditingKey(`proc-${k}-${newIdx}`);
                                            }}
                                        />
                                    </li>
                                </ul>
                            </details>
                        </li>
                    )}
                    <li className="my-3">
                        <button
                            children="Add Process Category"
                            type="button"
                            className="btn btn-info"
                            onClick={() => {
                                const newIdx = curSettings.proc.length;
                                setCurSettings({ ...curSettings, proc:
                                    [...curSettings.proc, { category: "", data: [] }]
                                });
                                setEditingKey(`cat-${newIdx}`);
                            }}
                        />
                    </li>
                </ul>
            </details>
            <Link
                children="Back"
                to=".."
                className="btn btn-outline-secondary float-end mb-3"
            />
        </div>
    );
}